import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';

/**
 * The persistent compact property selector. S8B §7, and it is the one piece of
 * the shell the specification names a failure mode for:
 *
 *   "Use a persistent compact property selector. Default switch behavior lands
 *    on the selected property's Attention. ... The user should not be required
 *    to return to Portfolio merely to change properties."
 *
 * Which is exactly what this product required until now. Every property screen
 * carried no property identity at all — §6 lists "current property identity"
 * first under Primary product context — so a manager with four buildings could
 * not tell from the shell which one they were reading, and changing building
 * meant going back to Portfolio and starting again.
 *
 * IT IS A SELECTOR, NOT A BREADCRUMB. §24 accepts "property selector,
 * breadcrumb, header or equivalent" as compact persistent context; this is the
 * first because it is the only one of the three that also *does* something.
 *
 * **Landing on Attention is F02 and is not negotiable.** Carrying the current
 * sub-route across a switch shows one building's Obligations screen under
 * another building's name, and in this product that is a page of the wrong
 * strata's statutory duties. The behaviour already existed on the route; this
 * gives it a control.
 *
 * **It renders nothing outside a property.** On Portfolio or My Work there is
 * no current property to name, and a selector showing "choose one" on a screen
 * that is already a list of all of them is noise.
 */

type Property = { id: string; name: string };

export function PropertySelector() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    api
      .properties<{ properties: Property[] }>()
      .then((r) => !cancelled && setProperties(r.properties))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  if (!propertyId) return null;

  const current = properties?.find((p) => p.id === propertyId);

  /* The name, the moment it is known, and the id until then.
   *
   * Not a spinner and not a blank: this is the answer to "which building am I
   * looking at", and a shell that withholds it while a list loads is a shell
   * that cannot be trusted on the screen where it matters. The id is ugly and
   * it is true. */
  const label = current?.name ?? (failed ? 'This property' : `${propertyId.slice(0, 8)}…`);

  return (
    <div className="property-selector">
      <span className="property-selector__label" id="property-selector-label">
        Property
      </span>
      {properties && properties.length > 1 ? (
        <select
          aria-labelledby="property-selector-label"
          value={propertyId}
          onChange={(e) => navigate(`/p/${e.target.value}/attention`)}
        >
          {/* A property the API did not return is still the one being read.
              Without this the select would silently show a different building
              as selected, which is the worst possible failure for a control
              whose entire job is saying which building you are in. */}
          {!current && <option value={propertyId}>{label}</option>}
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      ) : (
        <strong className="property-selector__name">{label}</strong>
      )}
    </div>
  );
}
